/* eslint-disable react/require-default-props */
import React, { forwardRef, useRef } from 'react';
import { Button as RBButton, Spinner, OverlayTrigger } from 'react-bootstrap';
import classNames from 'classnames';

import { PopoverTooltip } from '../PopoverTooltipV2/PopoverTooltipV2';
import { ButtonVariants, RBButtonPropsExtended, RBButtonTypeExtended } from './ButtonV2.types';
import { IFileInputProps, IPopoverTooltipPropsExtended } from './ButtonV2.interfaces';
import { IIconProps } from '../interfaces/general.interface';

import './ButtonV2.scss';

export interface IButtonProps extends RBButtonPropsExtended {
  form?: string;
  type?: RBButtonTypeExtended;
  disabled?: boolean;
  isLoading?: boolean;
  variant?: ButtonVariants;
  icon?: IIconProps;
  fileInput?: IFileInputProps;
  toolTip?: Omit<IPopoverTooltipPropsExtended, 'id'> & {
    id?: string; // Update to make id optional
  };
  testId?: string;
}

/**
 * Button Component - A specialized component designed to handle scenarios
 * where it can either act as a link or a button with a click handler.
 *
 * If an `href` is provided, the component will act as a link, and any `onClick` handler
 * will be ignored. If no `href` is provided, the component will act as a button, and
 * the `onClick` handler will be called when the button is clicked.
 */

const Button = forwardRef(
  (
    {
      className,
      children,
      href,
      form,
      type,
      disabled,
      isLoading,
      variant,
      icon,
      fileInput,
      toolTip,
      testId,
      onClick,
      ...rest
    }: IButtonProps,
    ref,
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const showTooltip = (toolTip?.title || toolTip?.paragraph) && !toolTip?.hide;

    // Use tooltip id if provided, otherwise generate one from paragraph or title
    const toolTipId =
      toolTip?.id || (toolTip?.paragraph ?? toolTip?.title)?.replace(/\s/g, '-') || '';

    const handleClick = (e) => {
      if (type === 'file') {
        fileInputRef.current?.click();
        return;
      }

      onClick?.(e);
    };

    const renderButton = () => (
      <RBButton
        ref={ref}
        bsPrefix="ButtonV2"
        href={href}
        form={form}
        type={type}
        variant={variant}
        disabled={disabled}
        className={classNames(className, {
          [`ButtonV2--icon-${icon?.position || 'right'}`]: icon?.name,
        })}
        data-testid={testId}
        onClick={!href ? handleClick : undefined}
        {...rest}
      >
        {children}

        {icon?.name && (
          <span
            className={classNames(
              `material-symbols-${icon.variant || 'sharp'}`,
              `icon-weight-${icon.weight || 'regular'}`,
            )}
          >
            {icon.name}
          </span>
        )}

        {type === 'file' && (
          <input
            ref={fileInputRef}
            type="file"
            accept={fileInput?.accept}
            onChange={fileInput?.onFileSelect}
          />
        )}

        {isLoading && <Spinner animation="border" size="sm" />}
      </RBButton>
    );

    return showTooltip ? (
      <OverlayTrigger
        delay={toolTip.delay}
        trigger={toolTip.trigger}
        placement={toolTip.placement}
        overlay={<PopoverTooltip id={toolTipId} {...toolTip} />}
      >
        {renderButton()}
      </OverlayTrigger>
    ) : (
      renderButton()
    );
  },
);

export default Button;
